'use client';

import React, {
  useState,
  useCallback,
  useEffect,
  createContext,
  useContext,
  type ReactNode,
} from 'react';
import { errorLogger } from '~/lib/error-logger';
import { consoleInterceptor } from '~/lib/console-interceptor';
import { disconnectSocket } from '~/lib/socket';
import { getSessionToken, clearSessionToken } from './useAuth';
import { apiClient } from '~/lib/api-client';

// ============= TYPE DEFINITIONS =============

export interface User {
  id: string;
  phone: string;
  country: string;
  name?: string;
  image?: string;
  address?: string;
  phoneVerified?: boolean;
  onboardingCompleted: boolean;
  halal: boolean;
  role: string;
  defaultFundAddress: string;
  createdAt?: Date | null;
}

export interface Session {
  user: User;
  hasApprovedPasskey: boolean;
  passkeyId?: string;
  token?: string;
}

interface SessionContextType {
  session: Session | null;
  status: 'loading' | 'authenticated' | 'unauthenticated';
  setSession: (session: Session | null) => void;
  clearSession: () => Promise<void>;
  updateSession: (updates: Partial<Session>) => void;
}

// ============= CONTEXT SETUP =============

const SessionContext = createContext<SessionContextType | undefined>(undefined);

// ============= SESSION PROVIDER =============

function useSessionProvider() {
  const [session, setSessionState] = useState<Session | null>(null);
  const [status, setStatus] = useState<'loading' | 'authenticated' | 'unauthenticated'>('loading');
  const [initialized, setInitialized] = useState(false);

  // ============= SESSION MANAGEMENT =============

  // Update error logger and console interceptor when session changes
  useEffect(() => {
    if (session?.user?.id) {
      errorLogger.setUserId(session.user.id);
      consoleInterceptor.setUserId(session.user.id);
    }
  }, [session?.user?.id]);

  const setSession = useCallback((newSession: Session | null) => {
    console.log('setSession called with:', newSession ? 'session data' : 'null');
    setSessionState(newSession);
    setStatus(newSession ? 'authenticated' : 'unauthenticated');
  }, []);

  const clearSession = useCallback(async () => {
    await clearSessionToken();
    setSession(null);
    errorLogger.setUserId('');
    consoleInterceptor.setUserId('');

    // TODO clear cache on logout

    // Disconnect socket on logout
    disconnectSocket();
  }, [setSession]);

  // ============= SESSION FETCHING =============

  const fetchSession = useCallback(
    async (token?: string): Promise<Session | null> => {
      try {
        const authToken = token || getSessionToken();
        console.log('fetchSession: authToken =', authToken ? 'present' : 'missing');
        
        if (!authToken) {
          console.log('fetchSession: No auth token, setting unauthenticated');
          setStatus('unauthenticated');
          return null;
        }

        console.log('fetchSession: Calling users.me');
        const result = await apiClient.get<Session>('users.me');

        if (result) {
          const userData = result?.user;
          if (userData) {
            const sessionData: Session = {
              token: authToken,
              user: userData,
              hasApprovedPasskey: result?.hasApprovedPasskey || false,
              passkeyId: result?.passkeyId,
            };

            setSession(sessionData);
            return sessionData;
          }
        }

        // If no valid data, clear session
        await clearSession();
        return null;
      } catch (error: any) {
        console.error('Error fetching session:', error);

        // Handle authentication errors
        if (error?.data?.code === 'UNAUTHORIZED' || error?.status === 401) {
          await clearSession();
        }

        return null;
      }
    },
    [setSession, clearSession]
  );

  const updateSession = useCallback(
    (updates: Partial<Session>) => {
      if (session) {
        setSession({ ...session, ...updates });
      }
    },
    [session, setSession]
  );

  // ============= INITIALIZATION =============

  // Initialize session on app start
  useEffect(() => {
    const initializeSession = async () => {
      try {
        setStatus('loading');
        await fetchSession();
      } catch (error) {
        console.error('Failed to initialize session:', error);
        setStatus('unauthenticated');
      } finally {
        setInitialized(true);
      }
    };

    // Only initialize once
    if (!initialized) {
      initializeSession();
    }
  }, [initialized, fetchSession]); // Remove fetchSession dependency

  // ============= RETURN OBJECT =============

  return {
    session,
    status,
    setSession,
    clearSession,
    updateSession,
    refetchSession: fetchSession
  };
}

// ============= PROVIDER COMPONENT =============

export function SessionProvider({ children }: { children: ReactNode }) {
  const session = useSessionProvider();
  return <SessionContext.Provider value={session}>{children}</SessionContext.Provider>;
}

// ============= HOOK =============

export function useSession() {
  const context = useContext(SessionContext);
  if (context === undefined) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return context;
}
